import { DialogComponent } from '@theme/dialog';

/**
 * A dialog that opens by itself shortly after the page loads.
 *
 * How often it reappears is set by the `data-frequency` attribute:
 *   - `session`  – once per browsing session (sessionStorage)
 *   - `once`     – once ever, per browser
 *   - `always`   – every page load, nothing stored
 *   - any number – once every N days (default 1)
 *
 * In the theme editor the popup always opens so merchants can style it.
 *
 * @extends DialogComponent
 */
class PopupComponent extends DialogComponent {
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    // Recorded on open rather than on close: clicking through the popup's link navigates away
    // without ever firing a close, and that still counts as having seen it.
    this.addEventListener('dialog:open', this.#markAsSeen);

    if (this.#hasBeenSeen) return;

    this.#timeout = setTimeout(() => this.showDialog(), this.delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timeout);
    this.removeEventListener('dialog:open', this.#markAsSeen);

    super.disconnectedCallback();
  }

  /**
   * Delay before the popup opens, in milliseconds.
   *
   * @returns {number}
   */
  get delay() {
    return Number(this.dataset.delay ?? 0);
  }

  /**
   * How often the popup may reappear.
   *
   * @returns {string}
   */
  get frequency() {
    return this.dataset.frequency ?? '1';
  }

  /**
   * Session-scoped frequencies live in sessionStorage, the rest in localStorage.
   *
   * @returns {Storage}
   */
  get #storage() {
    return this.frequency === 'session' ? sessionStorage : localStorage;
  }

  /**
   * Storage key, scoped to the section so two popups don't dismiss each other.
   *
   * @returns {string}
   */
  get #storageKey() {
    return `popup-dismissed-${this.dataset.sectionId}`;
  }

  /**
   * Whether the popup has already been seen within its frequency window.
   * Always false in the theme editor.
   *
   * @returns {boolean}
   */
  get #hasBeenSeen() {
    if (window.Shopify?.designMode) return false;
    if (this.frequency === 'always') return false;

    try {
      const seenAt = this.#storage.getItem(this.#storageKey);

      if (!seenAt) return false;
      if (this.frequency === 'session' || this.frequency === 'once') return true;

      // Day-based: reappear once the interval has elapsed. A malformed or future-dated value
      // (clock change, manual edit) is treated as unseen rather than hiding the popup forever.
      const days = Number(this.frequency);
      const elapsed = Date.now() - Number(seenAt);

      if (!Number.isFinite(days) || !Number.isFinite(elapsed) || elapsed < 0) return false;

      return elapsed < days * 24 * 60 * 60 * 1000;
    } catch {
      // Storage can throw when cookies are blocked; showing the popup is the safer fallback.
      return false;
    }
  }

  #markAsSeen = () => {
    if (this.frequency === 'always') return;

    try {
      this.#storage.setItem(this.#storageKey, String(Date.now()));
    } catch {
      // Ignore: the popup simply reappears on the next page view.
    }
  };
}

if (!customElements.get('popup-component')) {
  customElements.define('popup-component', PopupComponent);
}
